import { FilterConfig, MainFilter } from 'components/tables/list-table/ListTableTypes';
import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil';
import { useLazyGetMinContactsListWithFiltersQuery } from 'store/api/contacts';
import { useLazyGetMattersListQuery } from 'store/api/matters';
import { ClassifierData } from 'types/classifiers';
import { t } from 'utils/getTranslations';
import { renderMatterIndicators } from 'utils/matterOptionRenderer';
import { TRANSFER_TYPES } from './types';

export type ClientAssetsTableVariant = 'global' | 'client-tab' | 'case-tab';

interface Options {
    variant: ClientAssetsTableVariant;
    partyId?: string;
    matterId?: string;
    currencyClassifier?: ClassifierData;
}

/**
 * The tabs fix the client or the case, so their filter is passive, and the drawer and the search drop it: the client
 * tab searches the case and the note, the case tab the note.
 */
export function getClientAssetsFilterConfig({ variant, partyId, matterId, currencyClassifier }: Options): FilterConfig {
    const transferType: MainFilter = {
        type: 'multiselect',
        label: t('client-assets.fields.transfer-type'),
        selector: 'transferType',
        options: TRANSFER_TYPES.map((type) => ({ label: t(`client-assets.transfer-type.${type}`), value: type })),
    };
    const client: MainFilter = {
        type: 'dynamicmultiselect',
        subType: 'contact',
        label: t('client-assets.fields.client'),
        selector: 'party.id',
        paginated: {
            fetch: useLazyGetMinContactsListWithFiltersQuery,
            searchField: ['name', 'partyNumber'],
            getOptionIdentifier: 'id',
            getOptionLabel: 'name',
        },
    };
    const matter: MainFilter = {
        type: 'dynamicmultiselect',
        label: t('client-assets.fields.case'),
        selector: 'matter.id',
        getOptionStartAdornment: renderMatterIndicators,
        paginated: {
            fetch: useLazyGetMattersListQuery,
            searchField: ['fullName', 'clientParty.name'],
            getOptionIdentifier: 'id',
            getOptionLabel: 'fullName',
            sort: ['clientParty.name,asc', 'name,asc'],
            groupByField: 'clientParty.name',
            filters: partyId ? `clientParty.id=in=(${partyId})` : undefined,
        },
    };
    const currency: MainFilter = {
        type: 'multiselect',
        label: t('client-assets.fields.currency'),
        selector: 'currency',
        options: getFilterOptionsFromClassifier(currencyClassifier),
    };
    const amount: MainFilter = {
        type: 'interval',
        label: t('client-assets.fields.transfer-amount'),
        selector: 'transferAmount',
        minLabel: t('client-assets.filters.amount-from'),
        maxLabel: t('client-assets.filters.amount-to'),
    };
    const bankTransferDate: MainFilter = {
        type: 'daterange',
        label: t('client-assets.filters.date-from'),
        labelTo: t('client-assets.filters.date-to'),
        selector: 'bankTransferDate',
        shouldDisplayQuickPick: true,
    };

    switch (variant) {
        case 'global':
            return {
                quickFilters: [],
                filters: [transferType, client, matter, currency, amount, bankTransferDate],
                searchBarSelectors: [{ selector: 'party.name' }],
            };
        case 'client-tab':
            return {
                quickFilters: [],
                filters: [transferType, matter, currency, amount, bankTransferDate],
                searchBarSelectors: [
                    { selector: 'matter.caseNumber' },
                    { selector: 'matter.name' },
                    { selector: 'note' },
                ],
                passiveFilter: `party.id==${partyId}`,
            };
        case 'case-tab':
            return {
                quickFilters: [],
                filters: [transferType, currency, amount, bankTransferDate],
                searchBarSelectors: [{ selector: 'note' }],
                passiveFilter: `matter.id==${matterId}`,
            };
    }
}
